import { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { assistantAPI } from '../../../features/marketplace/services/api/assistantAPI';
import {
  getAssistantsQueryOptions,
  getDefaultModelQueryOptions,
} from '../../../features/marketplace/services/queries/assistantApiQueries';

/** Confirmed denial for an assistant requested through a shared link. */
export interface AccessRequest {
  assistantId: string;
  assistantName: string;
  ownerEmail: string | null;
}

interface ChatRouteLoaderArgs {
  context: { queryClient: QueryClient };
  deps: { assistant?: string; chapter: string[] };
}

/**
 * Resolves the assistant named in the URL. Accessible assistants open
 * directly. An existing assistant missing from the caller's list is a
 * confirmed denial and yields `accessRequest`. Nonexistent assistants and
 * lookup failures keep the redirect to `/chat` and never expose owner details.
 */
export const loadChatRoute = async ({ context, deps }: ChatRouteLoaderArgs) => {
  const assistantDetails = await context.queryClient.ensureQueryData(
    getAssistantsQueryOptions()
  );
  const defaultModelData = await context.queryClient.ensureQueryData(
    getDefaultModelQueryOptions()
  );
  const assistantMatch = assistantDetails.find(
    (a) => a.name === deps.assistant
  );

  let accessRequest: AccessRequest | null = null;
  if (deps.assistant && !assistantMatch) {
    try {
      const metadata = await assistantAPI.getAccessRequestMetadataAsync(
        deps.assistant
      );
      accessRequest = {
        assistantId: deps.assistant,
        assistantName: metadata.standardName ?? deps.assistant,
        ownerEmail: metadata.ownerEmail ?? null,
      };
    } catch {
      throw redirect({ to: '/chat', replace: true });
    }
  }

  return {
    assistantDetails,
    defaultModelData,
    assistantFromUrl: assistantMatch ?? null,
    selectedChaptersFromUrl: deps.chapter,
    accessRequest,
  };
};
